import EventIcon from '@mui/icons-material/Event';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonIcon from '@mui/icons-material/Person';
import { Box, ListItem, ListItemButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ListChildComponentProps } from 'react-window';

import { BaseAccidentV2 } from '../../../../backendsdk';
import useProfile from '../../../../hooks/profile';
import palette from '../../../ColorPalette';

interface ListRowData {
    accidents: BaseAccidentV2[];
    accidentIdsWithNotifications: number[];
    changeNotificationStatus: CallableFunction;
    getLicensePlateLabel: (accident: BaseAccidentV2) => string;
}

const AccidentListRow: React.FC<ListChildComponentProps<ListRowData>> = (props) => {
    const { index, data, style } = props;
    const { accidents } = data;
    const { t } = useTranslation();
    const { profile } = useProfile();
    const history = useHistory();
    const accident = accidents[index];
    const hasNotification = data.accidentIdsWithNotifications.includes(accident.accident_id);

    return (
        <ListItem
            className="accident-list-item"
            data-testid="accident-list-item"
            style={style}
            key={accident.accident_id}
            disablePadding
            disableGutters
            sx={{
                borderBottomWidth: 1,
                borderBottomStyle: 'solid',
                borderBottomColor: 'lightGrayColor.main',
                direction: 'ltr',
                position: 'relative',
                height: 105,
            }}
        >
            <ListItemButton
                id={`accident-list-item-${accident.accident_id}`}
                data-testid={`accident-list-item-${accident.accident_id}`}
                onClick={() => {
                    hasNotification ? data.changeNotificationStatus(accident.accident_id, true) : null;
                    history.push(`/accidents/${accident.accident_id}`);
                }}
                sx={{ height: '100%' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        height: '100%',
                        justifyContent: 'space-around',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ color: palette.primary, mr: 1 }} />
                            <Typography
                                sx={{ color: !!accident.driver_name ? palette.black : palette.neutral[500] }}
                                data-testid={'driver_name-value'}
                            >
                                {`${
                                    !!accident.driver_name
                                        ? accident.driver_name
                                        : t('content.accidents.unknown_driver')
                                }${!!accident.sub_fleet_name ? ` (${accident.sub_fleet_name})` : ''}`}
                            </Typography>
                        </Box>
                        <Typography
                            fontSize={12}
                            sx={{ color: palette.neutral[500] }}
                        >{`#${accident.accident_id}`}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocalShippingIcon sx={{ color: palette.primary, mr: 1 }} />
                        <Typography>{data.getLicensePlateLabel(accident)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventIcon sx={{ color: palette.primary, mr: 1 }} />
                        <Typography>{dayjs.unix(accident.timestamp).format(profile.dateTimeFormat)}</Typography>
                    </Box>
                </Box>
                {hasNotification ? (
                    <Box
                        id={`${accident.accident_id}-notification-icon`}
                        data-testid="notification-icon"
                        sx={{
                            position: 'absolute',
                            bottom: 10,
                            right: 16,
                            backgroundColor: 'secondary.main',
                            borderRadius: '50%',
                            border: `1px solid ${palette.primary}`,
                            padding: '2px',
                            width: '35px',
                            height: '35px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <NotificationsActiveIcon
                            sx={{
                                color: 'primary.main',
                                transform: 'rotate(25deg)',
                            }}
                        />
                    </Box>
                ) : null}
            </ListItemButton>
        </ListItem>
    );
};

export default AccidentListRow;
